import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faRotateLeft,
  type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { CustomColorPickerBottomSheet } from "components/colorpicker";
import {
  EmailBuilderColorContrastHint,
  type EmailBuilderContrastHintPreset,
} from "../../../settings/EmailBuilderColorContrastHint";
import { EmailBuilderSettingsToggleField } from "../../../settings/controls/EmailBuilderSettingsToggleField";
import type {
  Align,
  ButtonSize,
  CornerRadius,
  ImageIntrinsicSize,
} from "../../../domain/types";
import {
  IMAGE_WIDTH_MIN_PX,
  initialCustomImageWidthPx,
  resolveImageWidthCommit,
} from "../../../domain/image-width";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import { useEmailBuilderSettingsOverlayPortalId } from "../../canvas/settings/emailBuilderSettingsOverlayPortal";
import { useEmailBuilderContinuousHistoryGroup } from "./emailBuilderContinuousHistoryGroup";
import { emailBuilderSettingsAutosaveGestureHandlers } from "../../../emailBuilderSettingsTextAutosaveDeferral";
import { useStableDomId, useStableScopedDomId } from "../../../useStableDomId";

const ALIGN_OPTIONS: Array<{ align: Align; icon: IconDefinition; label: string }> = [
  { align: "left", icon: faAlignLeft, label: "Left" },
  { align: "center", icon: faAlignCenter, label: "Center" },
  { align: "right", icon: faAlignRight, label: "Right" },
];

interface IEmailBuilderBlockSettingsIconButtonProps {
  label: string;
  icon: IconDefinition;
  onClick: () => void;
  variant?: "default" | "danger";
  disabled?: boolean;
  "aria-expanded"?: boolean;
}

export function EmailBuilderBlockSettingsIconButton(props: IEmailBuilderBlockSettingsIconButtonProps) {
  const { label, icon, onClick, variant = "default", disabled = false } = props;
  const tooltipId = useStableScopedDomId("email-builder-block-settings-icon", label);
  const ariaExpanded = props["aria-expanded"];

  return (
    <OverlayTrigger
      placement="left"
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{label}</Tooltip>}
    >
      <button
        type="button"
        className={classNames("email-builder-block-settings-icon-button", {
          "email-builder-block-settings-icon-button--danger": variant === "danger",
        })}
        aria-label={label}
        aria-expanded={ariaExpanded}
        disabled={disabled}
        onClick={onClick}
      >
        <FontAwesomeIcon icon={icon} aria-hidden="true" />
      </button>
    </OverlayTrigger>
  );
}

export type TEmailBuilderBoundedRangeProps = {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number | undefined;
  onChange: (value: number | undefined, historyGroupId?: string | null) => void;
  /** When set, slider shows inheritValue when value is undefined; reset clears the override. */
  inheritValue?: number;
  /** When set (and inheritValue is not), reset restores this value. */
  defaultValue?: number;
};

export function EmailBuilderBoundedRange(props: TEmailBuilderBoundedRangeProps) {
  const { t } = useTranslation();
  const { label, min, max, step = 1, value, onChange, inheritValue, defaultValue } = props;
  const labelId = useStableDomId("email-builder-bounded-range-label");
  const continuousHistory = useEmailBuilderContinuousHistoryGroup();
  const autosaveGestureHandlers = emailBuilderSettingsAutosaveGestureHandlers(continuousHistory);
  const inheritMode = inheritValue !== undefined;
  const displayValue = inheritMode ? (value ?? inheritValue) : (value ?? defaultValue ?? min);
  const showReset = inheritMode
    ? value !== undefined
    : defaultValue !== undefined && value !== defaultValue;

  function handleReset() {
    continuousHistory.end();
    if (inheritMode) {
      onChange(undefined, null);
    } else if (defaultValue !== undefined) {
      onChange(defaultValue, null);
    }
  }

  function handleSetValue(next: number) {
    onChange(next, continuousHistory.noteChange());
  }

  return (
    <div className="email-builder-block-settings-section email-builder-block-settings-bounded-range">
      <div className="email-builder-block-settings-color-row">
        <div id={labelId} className="email-builder-block-settings-label">
          {label}
        </div>
        <div className="email-builder-block-settings-label-row-actions email-builder-block-settings-label-row-actions--slots-1">
          {showReset ? (
            <EmailBuilderBlockSettingsIconButton
              label={t("Reset to default")}
              icon={faRotateLeft}
              onClick={handleReset}
            />
          ) : (
            <span className="email-builder-block-settings-icon-button-slot" aria-hidden="true" />
          )}
        </div>
      </div>
      <div role="group" aria-labelledby={labelId}>
        <WebsiteBuilderRange
          min={min}
          max={max}
          step={step}
          value={displayValue}
          setValue={handleSetValue}
          {...autosaveGestureHandlers}
        />
      </div>
    </div>
  );
}

interface IEmailBuilderIconSliderRowProps {
  icon: IconDefinition;
  iconFlipX?: boolean;
  ariaLabel: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (value: number, historyGroupId?: string | null) => void;
  mixed?: boolean;
}

export function EmailBuilderIconSliderRow(props: IEmailBuilderIconSliderRowProps) {
  const { t } = useTranslation();
  const {
    icon,
    iconFlipX = false,
    ariaLabel,
    min,
    max,
    step = 1,
    value,
    onChange,
    mixed = false,
  } = props;
  const continuousHistory = useEmailBuilderContinuousHistoryGroup();
  const autosaveGestureHandlers = emailBuilderSettingsAutosaveGestureHandlers(continuousHistory);

  return (
    <div className="email-builder-icon-slider-row">
      <div
        className="email-builder-icon-slider-row__main"
        role="group"
        aria-label={ariaLabel}
      >
        <div
          className={classNames("email-builder-icon-slider-row__icon", {
            "email-builder-icon-slider-row__icon--flip-x": iconFlipX,
          })}
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="email-builder-icon-slider-row__range">
          <WebsiteBuilderRange
            min={min}
            max={max}
            step={step}
            value={value}
            setValue={next => {
              onChange(next, continuousHistory.noteChange());
            }}
            mixed={mixed}
            mixedLabel={mixed ? t("Mixed") : undefined}
            {...autosaveGestureHandlers}
          />
        </div>
      </div>
    </div>
  );
}

interface IEmailBuilderAlignmentControlProps {
  value: Align;
  onChange: (align: Align) => void;
}

export function EmailBuilderAlignmentControl(props: IEmailBuilderAlignmentControlProps) {
  const { t } = useTranslation();
  const { value, onChange } = props;
  const labelId = useStableDomId("email-builder-alignment-label");

  return (
    <div className="email-builder-settings-inline-field">
      <div id={labelId} className="email-builder-settings-inline-field__label">
        {t("Alignment")}
      </div>
      <div className="email-builder-settings-inline-field__control">
        <div
          className="email-builder-settings-segment-wide email-builder-settings-segment-wide--with-icons"
          role="radiogroup"
          aria-labelledby={labelId}
        >
          {ALIGN_OPTIONS.map(option => (
            <button
              key={option.align}
              type="button"
              role="radio"
              aria-checked={value === option.align}
              aria-label={t(option.label)}
              data-on={value === option.align ? "true" : "false"}
              onClick={() => onChange(option.align)}
            >
              <FontAwesomeIcon icon={option.icon} aria-hidden="true" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface IEmailBuilderBinarySegmentControlProps {
  label: string;
  falseLabel: string;
  trueLabel: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export function EmailBuilderBinarySegmentControl(props: IEmailBuilderBinarySegmentControlProps) {
  const { t } = useTranslation();
  const { label, falseLabel, trueLabel, value, onChange } = props;
  const labelId = useStableDomId("email-builder-binary-segment-label");

  const options = [
    { segmentValue: false, label: falseLabel },
    { segmentValue: true, label: trueLabel },
  ];

  return (
    <div className="email-builder-settings-inline-field">
      <div id={labelId} className="email-builder-settings-inline-field__label">
        {label}
      </div>
      <div className="email-builder-settings-inline-field__control">
        <div
          className="email-builder-settings-segment-wide"
          role="radiogroup"
          aria-labelledby={labelId}
        >
          {options.map(option => (
            <button
              key={String(option.segmentValue)}
              type="button"
              role="radio"
              aria-checked={value === option.segmentValue}
              data-on={value === option.segmentValue ? "true" : "false"}
              onClick={() => onChange(option.segmentValue)}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface IEmailBuilderButtonWidthControlProps {
  fullWidth: boolean;
  fullWidthMobile: boolean;
  onChange: (patch: { fullWidth?: boolean; fullWidthMobile?: boolean }) => void;
}

export function EmailBuilderButtonWidthControl(props: IEmailBuilderButtonWidthControlProps) {
  const { t } = useTranslation();
  const { fullWidth, fullWidthMobile, onChange } = props;

  return (
    <>
      <EmailBuilderBinarySegmentControl
        label={t("Width")}
        falseLabel={t("Fit to text")}
        trueLabel={t("Full width")}
        value={fullWidth}
        onChange={nextFullWidth => onChange({ fullWidth: nextFullWidth })}
      />
      {!fullWidth ? (
        <EmailBuilderBinarySegmentControl
          label={t("Mobile")}
          falseLabel={t("Fit to text")}
          trueLabel={t("Full width")}
          value={fullWidthMobile}
          onChange={nextFullWidthMobile => onChange({ fullWidthMobile: nextFullWidthMobile })}
        />
      ) : null}
    </>
  );
}

interface IEmailBuilderImageWidthControlProps {
  widthPx: number | null;
  fullWidthMobile: boolean;
  intrinsicSize: ImageIntrinsicSize | null;
  contentMaxWidth: number;
  widthSliderMax: number;
  onChange: (patch: { widthPx?: number | null; fullWidthMobile?: boolean }) => void;
}

export function EmailBuilderImageWidthControl(props: IEmailBuilderImageWidthControlProps) {
  const { t } = useTranslation();
  const {
    widthPx,
    fullWidthMobile,
    intrinsicSize,
    contentMaxWidth,
    widthSliderMax,
    onChange,
  } = props;
  const isCustomWidth = widthPx !== null;
  const sliderMax = Math.max(widthSliderMax, IMAGE_WIDTH_MIN_PX);

  function patchWidthMode(custom: boolean) {
    if (custom && !isCustomWidth) {
      onChange({
        widthPx: initialCustomImageWidthPx(intrinsicSize, contentMaxWidth),
        fullWidthMobile: false,
      });
      return;
    }
    if (!custom && isCustomWidth) {
      onChange({ widthPx: null, fullWidthMobile: false });
    }
  }

  if (contentMaxWidth <= 0) {
    return null;
  }

  return (
    <>
      <EmailBuilderBinarySegmentControl
        label={t("Width")}
        falseLabel={t("Full width")}
        trueLabel={t("Custom")}
        value={isCustomWidth}
        onChange={patchWidthMode}
      />
      {isCustomWidth ? (
        <>
          <EmailBuilderBoundedRange
            label={t("Width (px)")}
            value={widthPx ?? undefined}
            min={IMAGE_WIDTH_MIN_PX}
            max={sliderMax}
            defaultValue={widthPx ?? IMAGE_WIDTH_MIN_PX}
            onChange={nextWidthPx => {
              if (nextWidthPx === undefined) {
                return;
              }
              onChange({
                widthPx: resolveImageWidthCommit(nextWidthPx, contentMaxWidth),
              });
            }}
          />
          <EmailBuilderSettingsToggleField
            label={t("Full width on mobile")}
            checked={fullWidthMobile}
            onChange={nextFullWidthMobile => onChange({ fullWidthMobile: nextFullWidthMobile })}
          />
        </>
      ) : null}
    </>
  );
}

const BUTTON_SIZE_OPTIONS: Array<{ size: ButtonSize; label: string }> = [
  { size: "sm", label: "Small" },
  { size: "md", label: "Medium" },
  { size: "lg", label: "Large" },
];

interface IEmailBuilderButtonSizeControlProps {
  value: ButtonSize;
  defaultValue?: ButtonSize;
  onChange: (size: ButtonSize, historyGroupId?: string | null) => void;
}

export function EmailBuilderButtonSizeControl(props: IEmailBuilderButtonSizeControlProps) {
  const { t } = useTranslation();
  const { value, defaultValue = "md", onChange } = props;
  const labelId = useStableDomId("email-builder-button-size-label");
  const showReset = value !== defaultValue;

  return (
    <div className="email-builder-settings-inline-field">
      <div className="email-builder-block-settings-color-row">
        <div id={labelId} className="email-builder-block-settings-label">
          {t("Button size")}
        </div>
        <div className="email-builder-block-settings-label-row-actions email-builder-block-settings-label-row-actions--slots-1">
          {showReset ? (
            <EmailBuilderBlockSettingsIconButton
              label={t("Reset to default")}
              icon={faRotateLeft}
              onClick={() => onChange(defaultValue, null)}
            />
          ) : (
            <span className="email-builder-block-settings-icon-button-slot" aria-hidden />
          )}
        </div>
      </div>
      <div className="email-builder-settings-inline-field__control">
        <div
          className="email-builder-settings-segment-wide"
          role="radiogroup"
          aria-labelledby={labelId}
        >
          {BUTTON_SIZE_OPTIONS.map(option => (
            <button
              key={option.size}
              type="button"
              role="radio"
              aria-checked={value === option.size}
              data-on={value === option.size ? "true" : "false"}
              onClick={() => onChange(option.size)}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function emailBuilderCornerRadiusIsUniform(value: CornerRadius): boolean {
  return (
    value.topLeft === value.topRight &&
    value.topRight === value.bottomRight &&
    value.bottomRight === value.bottomLeft
  );
}

interface IEmailBuilderColorOverrideFieldProps {
  label: string;
  color: string;
  hasOverride: boolean;
  onChange: (color: string, historyGroupId?: string | null) => void;
  onReset: () => void;
  /** When false, label appears only on the picker row (use under a settings group title). */
  showFieldLabel?: boolean;
  /**
   * When true and there is no override, show the transparent/none slash swatch
   * instead of previewing an inherited color (used for optional backgrounds).
   */
  emptyAsNone?: boolean;
  contrastAgainst?: string;
  contrastLargeText?: boolean;
  contrastPreset?: EmailBuilderContrastHintPreset;
}

export function EmailBuilderColorOverrideField(props: IEmailBuilderColorOverrideFieldProps) {
  const { t } = useTranslation();
  const {
    label,
    color,
    hasOverride,
    onChange,
    onReset,
    emptyAsNone = false,
    showFieldLabel = true,
    contrastAgainst,
    contrastLargeText = false,
    contrastPreset = "block-surface",
  } = props;
  const overlayPortalId = useEmailBuilderSettingsOverlayPortalId();
  const continuousHistory = useEmailBuilderContinuousHistoryGroup();
  const autosaveGestureHandlers = emailBuilderSettingsAutosaveGestureHandlers(continuousHistory);
  const swatchColor = emptyAsNone && !hasOverride ? (null as unknown as string) : color;

  function handleReset() {
    continuousHistory.end();
    onReset();
  }

  function handleColorChange(nextColor: string | null) {
    if (nextColor === null) {
      return;
    }
    onChange(nextColor, continuousHistory.noteChange());
  }

  const resetControl = hasOverride ? (
    <EmailBuilderBlockSettingsIconButton
      label={t("Reset to default")}
      icon={faRotateLeft}
      onClick={handleReset}
    />
  ) : null;

  const picker = (
    <>
      <CustomColorPickerBottomSheet
        title={label}
        hexColor={swatchColor}
        updateHexColor={handleColorChange}
        portalParentId={overlayPortalId}
        fullWidth
        {...autosaveGestureHandlers}
      />
      <EmailBuilderColorContrastHint
        color={color}
        contrastAgainst={contrastAgainst}
        largeText={contrastLargeText}
        preset={contrastPreset}
      />
    </>
  );

  if (!showFieldLabel) {
    return (
      <div className="email-builder-settings-color-field">
        <div className="email-builder-settings-color-field__header">
          <div className="email-builder-block-settings-label">{label}</div>
          {resetControl}
        </div>
        {picker}
      </div>
    );
  }

  return (
    <div className="email-builder-block-settings-section">
      <div className="email-builder-block-settings-color-row">
        <div className="email-builder-block-settings-label">{label}</div>
        {resetControl}
      </div>
      {picker}
    </div>
  );
}
